import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, type SipDto, type SipsResponse } from '../lib/api.js';
import { formatCompact } from '../components/MoneyText.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';

/**
 * The SIP register.
 *
 * No statement has a "SIP" column — a systematic plan is visible only as a
 * rhythm in the transactions, so everything on this screen is INFERRED. The
 * screen says so, and shows the evidence (how many instalments, when the last
 * one landed) beside every claim, because a schedule the user cannot check is
 * a schedule they cannot trust.
 *
 * Read-only, like the rest of the product: it reports the plans the documents
 * reveal and never starts, stops or alters a mandate.
 */
const CADENCE_LABEL: Record<string, string> = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  irregular: 'irregular',
};

/** "5th", "21st" — the instalment day reads as a date, not as a number. */
function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
  return `${day}${['th', 'st', 'nd', 'rd'][day % 10] ?? 'th'}`;
}

export function Sips() {
  const [params, setParams] = useSearchParams();
  const showStopped = params.get('show') === 'stopped';

  const { data, isPending, error } = useQuery({
    queryKey: ['sips'],
    queryFn: () => api.get<SipsResponse>('/sips'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const active = data.sips.filter((s) => s.status === 'active');
  const stopped = data.sips.filter((s) => s.status === 'stopped');
  const shown = showStopped ? stopped : active;

  return (
    <div>
      <h1 className="page-title">SIPs</h1>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div className="hero-money" style={{ font: 'var(--type-title1)' }}>
          {formatCompact(data.monthlyCommitment.amount)}
          <span className="page-sub" style={{ display: 'inline', font: 'var(--type-footnote)' }}>
            {' '}
            / month
          </span>
        </div>
        <p className="page-sub" style={{ marginBottom: 0 }}>
          {data.activeCount} active plan{data.activeCount === 1 ? '' : 's'}
          {/*
            A quarterly plan is counted as a third of its instalment each month
            rather than whole, so this figure is what actually leaves the bank in
            a typical month.
          */}
          {' · '}spread across the months they fall in
        </p>
      </div>

      <div className="chiprow">
        <button
          className={`chip${!showStopped ? ' chip-active' : ''}`}
          aria-pressed={!showStopped}
          onClick={() => setParams({})}
        >
          Running <span className="chip-value">{active.length}</span>
        </button>
        <button
          className={`chip${showStopped ? ' chip-active' : ''}`}
          aria-pressed={showStopped}
          onClick={() => setParams({ show: 'stopped' })}
        >
          Stopped <span className="chip-value">{stopped.length}</span>
        </button>
      </div>

      {shown.length === 0 ? (
        <Card title={showStopped ? 'Nothing stopped' : 'No plans found'}>
          <div className="empty">
            <span className="empty-glyph" aria-hidden>
              ↻
            </span>
            {showStopped
              ? 'Every instalment plan we can see is still running.'
              : 'Plans are recognised from repeated purchases in your statements. If a SIP started recently, it takes three instalments before it can be told apart from ordinary buying.'}
          </div>
        </Card>
      ) : (
        <Card title={showStopped ? 'Stopped' : 'Running'}>
          {shown.map((s) => (
            <SipRow key={s.id} sip={s} />
          ))}
        </Card>
      )}

      <p className="page-sub" style={{ marginTop: 'var(--space-4)' }}>
        Plans are worked out from the pattern of purchases in your statements, not read from a
        field — no statement carries one. A plan is called stopped only when the statement looked
        well past the next expected instalment and did not find it.
      </p>
    </div>
  );
}

function SipRow({ sip: s }: { sip: SipDto }) {
  return (
    <Row
      left={
        <span>
          <span className="row-title">{s.scheme.name}</span>
          <span className="row-meta">
            {s.member.name} · {s.folio}
            {s.member.unmatched && <span className="state-warn"> · unmatched</span>}
          </span>
          <span className="row-detail">
            {/* The evidence, next to the claim it supports. */}
            {s.instalments} instalment{s.instalments === 1 ? '' : 's'} since {s.firstInstalment.slice(0, 7)} ·
            last {s.lastInstalment}
          </span>
        </span>
      }
      right={
        <span>
          <span className="money-cell">{formatCompact(s.amount.amount)}</span>
          <span className="row-detail">
            {CADENCE_LABEL[s.cadence] ?? s.cadence}, {ordinal(s.dayOfMonth)}
          </span>
        </span>
      }
    />
  );
}
